import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Container, Typography, CircularProgress, Box } from '@mui/material';
import { exchangeCodeForTokens, parseJwt } from '../../utils/auth';
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth

const Callback: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleAuthenticationResult } = useAuth(); // Get handleAuthenticationResult from AuthContext
  const [error, setError] = useState<string | null>(null);
  const codeProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');

        if (!code) {
          setError('No authorization code found in the callback URL');
          return;
        }

        if (codeProcessed.current) return;
        codeProcessed.current = true;

        const tokens = await exchangeCodeForTokens(code);
        const userClaims = parseJwt(tokens.id_token);

        // Use handleAuthenticationResult to update AuthContext and sessionStorage
        handleAuthenticationResult(tokens.access_token, tokens.id_token, userClaims);

        navigate('/', { replace: true });
      } catch (err) {
        console.error('Error during authentication callback:', err);
        setError(`Authentication failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };

    handleCallback();
  }, [location.search, navigate, handleAuthenticationResult]); // Add handleAuthenticationResult to dependency array

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography color="error">{error}</Typography>
        <Typography sx={{ mt: 2 }}>
          <a href="/login">Return to login</a>
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography>Completing authentication, please wait...</Typography>
      </Box>
    </Container>
  );
};

export default Callback;
